import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import ImageCropModal from './ImageCropModal';
import { 
  X, User, MapPin, Link2, Briefcase, GraduationCap, Award, 
  Plus, Trash2, Loader2, Check, Image, Globe, 
  Linkedin, Twitter, ExternalLink, Save, Upload, Camera
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Universal URL resolver
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.includes('preview.emergentagent.com/api/uploads')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) return `${BACKEND_URL}${match[0]}`;
  }
  return url;
};

const ProfileEditModal = ({ isOpen, onClose, profile, onSave, initialTab }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const bioRef = useRef(null);
  const skillInputRef = useRef(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropType, setCropType] = useState('avatar'); // 'avatar' or 'banner'

  // Map ?edit=... key → tab id (Q4 — contextual CTA)
  useEffect(() => {
    if (!isOpen || !initialTab) return;
    const tabMap = {
      avatar: 'basic',
      photo: 'basic',  // Brief Vague 2 §5 — alias used by HomeChecklist (?edit=photo)
      bio: 'basic',
      banner: 'basic',
      languages: 'basic',
      experience: 'experience',
      education: 'education',
      skills: 'skills',
    };
    setActiveTab(tabMap[initialTab] || 'basic');
    // After mount, trigger the right action
    const t = setTimeout(() => {
      if ((initialTab === 'avatar' || initialTab === 'photo') && avatarInputRef.current) {
        avatarInputRef.current.click();
      } else if (initialTab === 'banner' && bannerInputRef.current) {
        bannerInputRef.current.click();
      } else if (initialTab === 'bio' && bioRef.current) {
        bioRef.current.focus();
        try { bioRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
      } else if (initialTab === 'skills' && skillInputRef.current) {
        // Brief Vague 2 §5 — onboarding deep-link : focus le champ Ajouter une compétence
        skillInputRef.current.focus();
        try { skillInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
      }
    }, 250);
    return () => clearTimeout(t);
  }, [isOpen, initialTab]);

  const [editData, setEditData] = useState({
    display_name: '',
    bio: '',
    avatar: '',
    banner_url: '',
    banner_gradient: { color1: '#667eea', color2: '#764ba2', direction: 'to right' },
    location: '',
    website: '',
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    instagram_url: '',
    youtube_url: '',
    reddit_url: '',
    experience: [],
    education: [],
    skills: [],
    languages: [],
    learning_interests: [],
    // Brief B5 P1 §7 — "Mon offre" : phrase courte + 3 tags
    expert_pitch: '',
    offer_tags: [],
    objectives: '',
    availability_status: 'available'
  });

  useEffect(() => {
    if (profile) {
      setEditData({
        display_name: profile.display_name || '',
        bio: profile.bio || '',
        avatar: profile.avatar || '',
        banner_url: profile.banner_url || '',
        banner_gradient: profile.banner_gradient || { color1: '#667eea', color2: '#764ba2', direction: 'to right' },
        location: profile.location || '',
        website: profile.website || '',
        linkedin_url: profile.linkedin_url || '',
        twitter_url: profile.twitter_url || '',
        facebook_url: profile.facebook_url || '',
        instagram_url: profile.instagram_url || '',
        youtube_url: profile.youtube_url || '',
        reddit_url: profile.reddit_url || '',
        experience: profile.experience || [],
        education: profile.education || [],
        skills: profile.skills || [],
        languages: profile.languages || [],
        learning_interests: profile.learning_interests || [],
        // Brief B5 P1 §7 — Mon offre
        expert_pitch: profile.expert_pitch || '',
        offer_tags: Array.isArray(profile.offer_tags) ? profile.offer_tags : [],
        objectives: profile.objectives || '',
        availability_status: profile.availability_status || 'available'
      });
    }
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, editData, { withCredentials: true });
      onSave(editData);
      onClose();
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar file selection -> open crop modal
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Veuillez selectionner une image'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('L\'image ne doit pas depasser 5 Mo'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropType('avatar');
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  // Handle banner file selection -> open crop modal
  const handleBannerUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Veuillez selectionner une image'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('L\'image ne doit pas depasser 10 Mo'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropType('banner');
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  // Upload cropped image to server
  const handleCropComplete = async (croppedFile) => {
    const isAvatar = cropType === 'avatar';
    if (isAvatar) setUploadingAvatar(true);
    else setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', croppedFile);
      const response = await axios.post(`${API}/upload/thumbnail`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.thumbnail_url) {
        if (isAvatar) {
          setEditData({ ...editData, avatar: response.data.thumbnail_url });
        } else {
          setEditData({ ...editData, banner_url: response.data.thumbnail_url });
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      if (isAvatar) setUploadingAvatar(false);
      else setUploadingBanner(false);
    }
  };

  // Experience management
  const addExperience = () => {
    setEditData({
      ...editData,
      experience: [...editData.experience, { title: '', company: '', years: '', description: '' }]
    });
  };

  const updateExperience = (index, field, value) => {
    const newExp = [...editData.experience];
    newExp[index] = { ...newExp[index], [field]: value };
    setEditData({ ...editData, experience: newExp });
  };

  const removeExperience = (index) => {
    setEditData({
      ...editData,
      experience: editData.experience.filter((_, i) => i !== index)
    });
  };

  // Education management
  const addEducation = () => {
    setEditData({
      ...editData,
      education: [...editData.education, { school: '', degree: '', years: '' }]
    });
  };

  const updateEducation = (index, field, value) => {
    const newEdu = [...editData.education];
    newEdu[index] = { ...newEdu[index], [field]: value };
    setEditData({ ...editData, education: newEdu });
  };

  const removeEducation = (index) => {
    setEditData({
      ...editData,
      education: editData.education.filter((_, i) => i !== index)
    });
  };

  // Skills management
  const [newSkill, setNewSkill] = useState('');
  // Brief B5 P1 §7 — offer tag input
  const [newOfferTag, setNewOfferTag] = useState('');
  const addSkill = () => {
    if (newSkill.trim() && !editData.skills.includes(newSkill.trim())) {
      setEditData({ ...editData, skills: [...editData.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setEditData({ ...editData, skills: editData.skills.filter(s => s !== skill) });
  };

  // Languages management
  const [newLanguage, setNewLanguage] = useState('');
  const addLanguage = () => {
    if (newLanguage.trim() && !editData.languages.includes(newLanguage.trim())) {
      setEditData({ ...editData, languages: [...editData.languages, newLanguage.trim()] });
      setNewLanguage('');
    }
  };

  const removeLanguage = (lang) => {
    setEditData({ ...editData, languages: editData.languages.filter(l => l !== lang) });
  };

  // Sprint 1.0c Jun 9 — learning_interests (dual-rôle "Explore aussi" pills)
  const [newInterest, setNewInterest] = useState('');
  const addInterest = () => {
    const t = newInterest.trim();
    if (t && !editData.learning_interests.includes(t)) {
      setEditData({ ...editData, learning_interests: [...editData.learning_interests, t] });
      setNewInterest('');
    }
  };
  const removeInterest = (tag) => {
    setEditData({ ...editData, learning_interests: editData.learning_interests.filter(t => t !== tag) });
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Infos de base', icon: User },
    { id: 'links', label: 'Liens sociaux', icon: Link2 },
    { id: 'experience', label: 'Expérience', icon: Briefcase },
    { id: 'education', label: 'Formation', icon: GraduationCap },
    { id: 'skills', label: 'Compétences', icon: Award }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="profile-edit-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Modifier le profil</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto shrink-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Info */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Avatar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Photo de profil</label>
                <div className="flex items-center gap-4">
                  {/* Avatar with upload overlay */}
                  <div className="relative group">
                    <Avatar className="h-24 w-24 ring-4 ring-gray-100">
                      <AvatarImage src={resolveUrl(editData.avatar)} />
                      <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-2xl">
                        {editData.display_name?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {/* Upload overlay */}
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      {uploadingAvatar ? (
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    {/* Upload button */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="w-full"
                    >
                      {uploadingAvatar ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Upload en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Importer une photo
                        </>
                      )}
                    </Button>
                    
                    {/* URL input as alternative */}
                    <div className="relative">
                      <Input
                        value={editData.avatar}
                        onChange={(e) => setEditData({ ...editData, avatar: e.target.value })}
                        placeholder="Ou collez une URL d'image..."
                        className="text-sm"
                      />
                    </div>
                    <p className="text-xs text-gray-400">Format accepté : JPG, PNG, GIF (max 5 Mo)</p>
                  </div>
                </div>
              </div>

              {/* Banner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bannière</label>
                {/* Banner preview with upload overlay */}
                <div 
                  className="relative group h-32 rounded-xl mb-3 overflow-hidden cursor-pointer"
                  onClick={() => bannerInputRef.current?.click()}
                  style={{
                    background: editData.banner_url 
                      ? `url(${resolveUrl(editData.banner_url)}) center/cover`
                      : `linear-gradient(${editData.banner_gradient.direction}, ${editData.banner_gradient.color1}, ${editData.banner_gradient.color2})`
                  }}
                >
                  {/* Upload overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                    {uploadingBanner ? (
                      <Loader2 className="h-8 w-8 text-white animate-spin" />
                    ) : (
                      <div className="text-center text-white">
                        <Camera className="h-8 w-8 mx-auto mb-1" />
                        <span className="text-sm font-medium">Changer la bannière</span>
                      </div>
                    )}
                  </div>
                </div>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                
                {/* Upload button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => bannerInputRef.current?.click()}
                  disabled={uploadingBanner}
                  className="w-full mb-3"
                >
                  {uploadingBanner ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer une bannière
                    </>
                  )}
                </Button>
                
                {/* URL input */}
                <Input
                  value={editData.banner_url || ''}
                  onChange={(e) => setEditData({ ...editData, banner_url: e.target.value })}
                  placeholder="Ou collez une URL d'image..."
                  className="mb-3"
                />
                
                {/* Gradient presets */}
                <p className="text-xs text-gray-500 mb-2">Ou choisissez un dégradé :</p>
                <div className="grid grid-cols-6 gap-2 mb-3">
                  {[
                    { color1: '#667eea', color2: '#764ba2', name: 'Violet' },
                    { color1: '#f093fb', color2: '#f5576c', name: 'Rose' },
                    { color1: '#4facfe', color2: '#00f2fe', name: 'Bleu' },
                    { color1: '#43e97b', color2: '#38f9d7', name: 'Vert' },
                    { color1: '#fa709a', color2: '#fee140', name: 'Sunset' },
                    { color1: '#a8edea', color2: '#fed6e3', name: 'Pastel' },
                    { color1: '#ff9a9e', color2: '#fecfef', name: 'Pêche' },
                    { color1: '#667eea', color2: '#f093fb', name: 'Mauve' },
                    { color1: '#30cfd0', color2: '#330867', name: 'Nuit' },
                    { color1: '#a18cd1', color2: '#fbc2eb', name: 'Lavande' },
                    { color1: '#ff6b6b', color2: '#feca57', name: 'Orange' },
                    { color1: '#5f72bd', color2: '#9b23ea', name: 'Indigo' }
                  ].map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setEditData({ 
                        ...editData, 
                        banner_url: '',
                        banner_gradient: { ...preset, direction: 'to right' }
                      })}
                      className={`h-10 rounded-lg transition-all hover:scale-105 hover:shadow-md ${
                        editData.banner_gradient?.color1 === preset.color1 && 
                        editData.banner_gradient?.color2 === preset.color2 && 
                        !editData.banner_url
                          ? 'ring-2 ring-purple-500 ring-offset-2' 
                          : ''
                      }`}
                      style={{ background: `linear-gradient(to right, ${preset.color1}, ${preset.color2})` }}
                      title={preset.name}
                    />
                  ))}
                </div>
                
                {/* Custom colors (optional) */}
                <details className="text-xs">
                  <summary className="text-gray-400 cursor-pointer hover:text-gray-600">Couleurs personnalisées</summary>
                  <div className="flex gap-2 mt-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Couleur 1</label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={editData.banner_gradient.color1}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            banner_gradient: { ...editData.banner_gradient, color1: e.target.value }
                          })}
                          className="w-10 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={editData.banner_gradient.color1}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            banner_gradient: { ...editData.banner_gradient, color1: e.target.value }
                          })}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Couleur 2</label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={editData.banner_gradient.color2}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            banner_gradient: { ...editData.banner_gradient, color2: e.target.value }
                          })}
                          className="w-10 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={editData.banner_gradient.color2}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            banner_gradient: { ...editData.banner_gradient, color2: e.target.value }
                          })}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom affiché</label>
                <Input
                  value={editData.display_name}
                  onChange={(e) => setEditData({ ...editData, display_name: e.target.value })}
                  placeholder="Votre nom"
                  data-testid="edit-display-name"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <textarea
                  ref={bioRef}
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                  placeholder="Parlez de vous en quelques mots..."
                  rows={3}
                  maxLength={300}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                  data-testid="edit-bio"
                />
                <p className="text-xs text-gray-400 text-right">{editData.bio.length}/300</p>
              </div>

              {/* Brief B5 P1 §7 — Mon offre (expert_pitch + offer_tags) */}
              <div className="rounded-xl border border-purple-100 dark:border-purple-900/30 bg-purple-50/40 dark:bg-purple-900/10 p-4 space-y-3" data-testid="edit-offer-section">
                <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                  <Award className="h-4 w-4" /> Mon offre
                </h4>
                <p className="text-xs text-gray-500 -mt-1">
                  Aide les visiteurs à comprendre en 2 secondes ce que tu proposes.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Ma phrase d'offre</label>
                  <textarea
                    value={editData.expert_pitch || ''}
                    onChange={(e) => setEditData({ ...editData, expert_pitch: e.target.value })}
                    placeholder="En une phrase, qu'est-ce que tu proposes ?"
                    rows={2}
                    maxLength={120}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none text-sm"
                    data-testid="edit-expert-pitch"
                  />
                  <p className="text-xs text-gray-400 text-right">{(editData.expert_pitch || '').length}/120</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Ce que je propose (3 max)</label>
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="edit-offer-tag-list">
                    {(editData.offer_tags || []).map((tag, idx) => (
                      <span
                        key={`offer-${idx}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold"
                        style={{ background: '#EEEDFE', color: '#3C3489' }}
                        data-testid={`edit-offer-tag-${idx}`}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => setEditData({ ...editData, offer_tags: editData.offer_tags.filter((_, i) => i !== idx) })}
                          className="hover:opacity-70"
                          data-testid={`edit-offer-tag-remove-${idx}`}
                          aria-label={`Supprimer ${tag}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newOfferTag}
                      onChange={(e) => setNewOfferTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = newOfferTag.trim();
                          if (v && (editData.offer_tags || []).length < 3 && !editData.offer_tags.includes(v)) {
                            setEditData({ ...editData, offer_tags: [...(editData.offer_tags || []), v] });
                            setNewOfferTag('');
                          }
                        }
                      }}
                      placeholder="Ex: Coaching 1-to-1"
                      disabled={(editData.offer_tags || []).length >= 3}
                      className="flex-1"
                      data-testid="edit-offer-tag-input"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const v = newOfferTag.trim();
                        if (v && (editData.offer_tags || []).length < 3 && !editData.offer_tags.includes(v)) {
                          setEditData({ ...editData, offer_tags: [...(editData.offer_tags || []), v] });
                          setNewOfferTag('');
                        }
                      }}
                      disabled={(editData.offer_tags || []).length >= 3 || !newOfferTag.trim()}
                      className="px-3 py-2 rounded-lg bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="edit-offer-tag-add"
                    >
                      Ajouter
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{(editData.offer_tags || []).length}/3</p>
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Localisation
                </label>
                <Input
                  value={editData.location}
                  onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                  placeholder="Ex: Genève, Suisse"
                  data-testid="edit-location"
                />
              </div>

              {/* Objectives */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Objectifs</label>
                <textarea
                  value={editData.objectives}
                  onChange={(e) => setEditData({ ...editData, objectives: e.target.value })}
                  placeholder="Je cherche à... / J'offre..."
                  rows={2}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                />
              </div>

              {/* Availability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilité</label>
                <div className="flex gap-3">
                  {[
                    { value: 'available', label: 'Disponible', color: 'green' },
                    { value: 'limited', label: 'Limité', color: 'yellow' },
                    { value: 'unavailable', label: 'Non disponible', color: 'red' }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setEditData({ ...editData, availability_status: opt.value })}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        editData.availability_status === opt.value
                          ? `border-${opt.color}-500 bg-${opt.color}-50 text-${opt.color}-700`
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Social Links */}
          {activeTab === 'links' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="h-4 w-4 inline mr-1" />
                  Site web
                </label>
                <Input
                  value={editData.website}
                  onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                  placeholder="https://votresite.com"
                  data-testid="edit-website"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Linkedin className="h-4 w-4 inline mr-1" />
                  LinkedIn
                </label>
                <Input
                  value={editData.linkedin_url}
                  onChange={(e) => setEditData({ ...editData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/votrenom"
                  data-testid="edit-linkedin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Twitter className="h-4 w-4 inline mr-1" />
                  Twitter / X
                </label>
                <Input
                  value={editData.twitter_url}
                  onChange={(e) => setEditData({ ...editData, twitter_url: e.target.value })}
                  placeholder="https://twitter.com/votrenom"
                  data-testid="edit-twitter"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="h-4 w-4 inline mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
                  </svg>
                  Facebook
                </label>
                <Input
                  value={editData.facebook_url || ''}
                  onChange={(e) => setEditData({ ...editData, facebook_url: e.target.value })}
                  placeholder="https://facebook.com/votrenom"
                  data-testid="edit-facebook"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="h-4 w-4 inline mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153.509.5.902 1.105 1.153 1.772.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 01-1.153 1.772c-.5.508-1.105.902-1.772 1.153-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 01-1.772-1.153 4.904 4.904 0 01-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 011.153-1.772A4.897 4.897 0 015.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 100 10 5 5 0 000-10zm6.5-.25a1.25 1.25 0 10-2.5 0 1.25 1.25 0 002.5 0zM12 9a3 3 0 110 6 3 3 0 010-6z"/>
                  </svg>
                  Instagram
                </label>
                <Input
                  value={editData.instagram_url || ''}
                  onChange={(e) => setEditData({ ...editData, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/votrenom"
                  data-testid="edit-instagram"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <svg className="h-4 w-4 inline mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube
                </label>
                <Input
                  value={editData.youtube_url || ''}
                  onChange={(e) => setEditData({ ...editData, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/@votrechaine"
                  data-testid="edit-youtube"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ExternalLink className="h-4 w-4 inline mr-1" />
                  Reddit
                </label>
                <Input
                  value={editData.reddit_url}
                  onChange={(e) => setEditData({ ...editData, reddit_url: e.target.value })}
                  placeholder="https://reddit.com/u/votrenom"
                  data-testid="edit-reddit"
                />
              </div>
            </div>
          )}

          {/* Experience */}
          {activeTab === 'experience' && (
            <div className="space-y-4">
              {editData.experience.map((exp, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-xl relative">
                  <button
                    onClick={() => removeExperience(index)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <Input
                      value={exp.title}
                      onChange={(e) => updateExperience(index, 'title', e.target.value)}
                      placeholder="Poste"
                    />
                    <Input
                      value={exp.company}
                      onChange={(e) => updateExperience(index, 'company', e.target.value)}
                      placeholder="Entreprise"
                    />
                  </div>
                  <Input
                    value={exp.years}
                    onChange={(e) => updateExperience(index, 'years', e.target.value)}
                    placeholder="Période (ex: 2020-2023)"
                    className="mb-3"
                  />
                  <textarea
                    value={exp.description || ''}
                    onChange={(e) => updateExperience(index, 'description', e.target.value)}
                    placeholder="Description (optionnel)"
                    rows={2}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none text-sm"
                  />
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addExperience}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une expérience
              </Button>
            </div>
          )}

          {/* Education */}
          {activeTab === 'education' && (
            <div className="space-y-4">
              {editData.education.map((edu, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-xl relative">
                  <button
                    onClick={() => removeEducation(index)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <Input
                    value={edu.school}
                    onChange={(e) => updateEducation(index, 'school', e.target.value)}
                    placeholder="École / Université"
                    className="mb-3"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={edu.degree}
                      onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                      placeholder="Diplôme"
                    />
                    <Input
                      value={edu.years}
                      onChange={(e) => updateEducation(index, 'years', e.target.value)}
                      placeholder="Années"
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addEducation}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une formation
              </Button>
            </div>
          )}

          {/* Skills */}
          {activeTab === 'skills' && (
            <div className="space-y-6">
              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Compétences</label>
                <div className="flex gap-2 mb-3">
                  <Input
                    ref={skillInputRef}
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Ajouter une compétence"
                    onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    data-testid="profile-edit-skill-input"
                  />
                  <Button onClick={addSkill} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editData.skills.map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                    >
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Langues</label>
                <div className="flex gap-2 mb-3">
                  <select
                    value={newLanguage}
                    onChange={(e) => {
                      const selected = e.target.value;
                      if (selected && !editData.languages.includes(selected)) {
                        setEditData({ ...editData, languages: [...editData.languages, selected] });
                        setNewLanguage('');
                      }
                    }}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">Sélectionner une langue...</option>
                    <option value="Français">Français</option>
                    <option value="English">English</option>
                    <option value="Deutsch">Deutsch</option>
                    <option value="Italiano">Italiano</option>
                    <option value="Español">Español</option>
                    <option value="Português">Português</option>
                    <option value="Nederlands">Nederlands</option>
                    <option value="Русский">Русский</option>
                    <option value="中文">中文</option>
                    <option value="日本語">日本語</option>
                    <option value="한국어">한국어</option>
                    <option value="العربية">العربية</option>
                    <option value="हिन्दी">हिन्दी</option>
                    <option value="Türkçe">Türkçe</option>
                    <option value="Polski">Polski</option>
                    <option value="Ελληνικά">Ελληνικά</option>
                    <option value="Svenska">Svenska</option>
                    <option value="Norsk">Norsk</option>
                    <option value="Dansk">Dansk</option>
                    <option value="Suomi">Suomi</option>
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editData.languages.map((lang, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                    >
                      {lang}
                      <button onClick={() => removeLanguage(lang)} className="hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Sprint 1.0c Jun 9 — Ce que j'explore (dual-rôle signal in the hero) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ce que j&apos;explore</label>
                <p className="text-xs text-gray-500 mb-2">Sujets pour lesquels tu cherches un expert. Affichés en pastilles vertes dans le hero de ton profil (max 3 visibles).</p>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    placeholder="Ex: Photographie, IA générative, Yoga…"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                    data-testid="profile-edit-interest-input"
                  />
                  <Button onClick={addInterest} variant="outline" data-testid="profile-edit-interest-add">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(editData.learning_interests || []).map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-1"
                      style={{ backgroundColor: '#E1F5EE', color: '#0F6E56' }}
                      data-testid={`profile-edit-interest-${i}`}
                    >
                      {tag}
                      <button onClick={() => removeInterest(tag)} className="hover:text-red-700">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-100 shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
            data-testid="save-profile"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={cropImageSrc}
        aspect={cropType === 'avatar' ? 1 : 4}
        onCropComplete={handleCropComplete}
        title={cropType === 'avatar' ? 'Recadrer la photo de profil' : 'Recadrer la banniere'}
      />
    </div>
  );
};

export default ProfileEditModal;
